import { useMemo, useState, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tab, Tabs, TextField, Tooltip, Typography,
} from '@mui/material'
import { CircleAlert, CircleCheck, Clock, Flag as FlagIcon, GitFork, Hexagon, MapPin, MessageSquareWarning, Sprout, Trash2, Undo2 } from 'lucide-react'
import {
  AppShell, Crumb, Flag as CountryFlag, SkeletonScreen, formatDate, formatDateTime, tokens,
} from '@agribot/polygon-checks-ui'
import { CompositionTree, OriginMap, countryName } from '@agribot/sourcing-model'
import { DiagramDialog } from '../components/TreeDialog'
import { SupplyDrill, SupplySummaryLine } from '../components/SupplyDrill'
import { useAddFlag, useApprove, useRemoveFlag, useReturn, useSubmission, useSubmissionDocuments, useSupplyGeojson } from '../hooks'
import { LEVEL_LABEL, LEVEL_ROLE, daysSince } from '../rules'
import type { Flag, FlagTarget, ReviewSubmission as Submission } from '../types'
import { StatusPill } from '../components/StatusPill'

const targetKey = (t: FlagTarget) =>
  t.kind === 'origin' ? `o:${t.materialId}:${t.ingredientId}:${t.originId}` : `c:${t.materialId}:${t.ingredientId}`

const pct = (v: number) => `${v.toLocaleString('en-GB', { maximumFractionDigits: 1 })}%`
const tonnes = (v: number) => `${v.toLocaleString('en-GB', { maximumFractionDigits: v < 10 ? 1 : 0 })} t`

/**
 * Screen 9: one supplier's submission. Its materials and compositions on the
 * left, every origin on a map on the right, coloured by check result, so a
 * wrong location is obvious at a glance. The reviewer flags any share or
 * origin with a note, then approves (on to the next level) or returns it to
 * the supplier with a comment.
 */
export function ReviewSubmission() {
  const { submissionId = '' } = useParams()
  const { data: s, isPending, error } = useSubmission(submissionId)

  if (isPending) return <AppShell crumbs={crumbs('…')}><SkeletonScreen /></AppShell>
  if (error || !s) {
    return (
      <AppShell crumbs={crumbs('Not found')}>
        <Box sx={{ p: 8 }}><Alert severity="error">{(error as Error | null)?.message ?? 'No such submission.'}</Alert></Box>
      </AppShell>
    )
  }
  return <Loaded s={s} />
}

const crumbs = (name: string) => [
  <Crumb key="a">Sourcing</Crumb>,
  <Crumb key="b" to="/sourcing/reviews">Reviews</Crumb>,
  <Crumb key="c">{name}</Crumb>,
]

function Loaded({ s }: { s: Submission }) {
  const [tab, setTab] = useState<'answers' | 'decisions' | 'history' | 'documents'>('answers')
  const { data: documents = [] } = useSubmissionDocuments(s.id)
  const [selected, setSelected] = useState<string | null>(null)
  const [deciding, setDeciding] = useState<'approve' | 'return' | null>(null)
  const level = s.level
  const open = level !== null
  const openFlags = s.flags.filter((f) => !f.addressed)
  const flaggedOrigins = useMemo(
    () => new Set(openFlags.flatMap((f) => (f.target.kind === 'origin' ? [f.target.originId] : []))),
    [openFlags],
  )
  const used = useMemo(() => {
    const ids = new Set(Object.values(s.answers).flatMap((a) => Object.values(a.allocations).flat().map((x) => x.originId)))
    return s.origins.filter((o) => ids.has(o.id))
  }, [s])
  const failed = s.failedChecks
  // The selected origin's plots on the map, when farms are linked to it.
  const selectedOrigin = used.find((o) => o.id === selected)
  const { data: plots } = useSupplyGeojson(selectedOrigin?.supply ? selectedOrigin.id : null)

  return (
    <AppShell
      crumbs={crumbs(s.supplier.name)}
      actions={open && level ? (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<Undo2 size={16} />} onClick={() => setDeciding('return')}>Return to supplier</Button>
          <Tooltip title={openFlags.length ? 'Remove the flags first, or return the submission with them.' : ''}>
            <span>
              <Button disabled={openFlags.length > 0} startIcon={<CircleCheck size={16} />} onClick={() => setDeciding('approve')}>
                {level === 1 ? 'Approve for final review' : 'Approve'}
              </Button>
            </span>
          </Tooltip>
        </Box>
      ) : undefined}
    >
      <Box sx={{ flex: 'none', px: 6, py: 4, borderBottom: `1px solid ${tokens.color.ink200}`, backgroundColor: 'background.paper', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, minWidth: 0 }}>
          <CountryFlag code={s.supplier.countryCode} size={18} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h3" component="h1" noWrap>{s.supplier.name}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {s.parent.name !== s.supplier.name ? `${s.parent.name} · ` : ''}{s.region} · {s.campaign.name}
            </Typography>
          </Box>
        </Box>
        <StatusPill status={s.status} />
        <Fact label="Submitted">{formatDate(s.submittedAt)} by {s.submittedBy}</Fact>
        {level && <Fact label={LEVEL_LABEL[level]}>{daysSince(s.levelSince)} days at this level</Fact>}
        <Fact label="Checks">
          {failed
            ? <Box component="span" sx={{ color: tokens.color.danger, display: 'inline-flex', gap: 1, alignItems: 'center' }}><CircleAlert size={14} strokeWidth={2} />{failed} origin{failed === 1 ? '' : 's'} failed</Box>
            : 'All origins passed'}
        </Fact>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
        <Box sx={{ width: 600, flex: 'none', display: 'flex', flexDirection: 'column', borderRight: `1px solid ${tokens.color.ink200}`, backgroundColor: 'background.default' }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 4, backgroundColor: 'background.paper' }}>
            <Tab value="answers" label="Answers" />
            <Tab value="decisions" label={`Decisions · ${s.reviews.length}`} />
            <Tab value="history" label={`History · ${s.history.length}`} />
            <Tab value="documents" label={`Documents · ${documents.length}`} />
          </Tabs>
          <Box sx={{ flex: 1, overflow: 'auto', p: 5 }}>
            {!open && (
              <Alert severity={s.status === 'approved' ? 'success' : 'info'} sx={{ mb: 4 }}>
                {s.status === 'approved' ? 'Approved. It is read-only now.' : s.status === 'returned' ? 'With the supplier. It comes back here when they resubmit.' : 'Not waiting for review.'}
              </Alert>
            )}
            {tab === 'answers' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {s.materials.map((m) => (
                  <MaterialCard key={m.id} s={s} materialId={m.id} open={open} selected={selected} onSelect={setSelected} />
                ))}
              </Box>
            )}
            {tab === 'decisions' && <Decisions s={s} />}
            {tab === 'history' && <History s={s} />}
            {tab === 'documents' && (
              documents.length === 0
                ? <Typography variant="body2" sx={{ color: 'text.secondary' }}>The supplier attached no documents.</Typography>
                : (
                  <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                    {documents.map((d) => (
                      <Box component="li" key={d.id} sx={{ py: 2.5, borderTop: `1px solid ${tokens.color.ink200}` }}>
                        <Box component="a" href={d.url} target="_blank" rel="noreferrer" sx={{ font: `500 14px ${tokens.font.sans}`, color: tokens.color.brandTeal, wordBreak: 'break-all' }}>{d.filename}</Box>
                        <Box sx={{ font: `400 12px ${tokens.font.sans}`, color: 'text.secondary' }}>
                          {s.materials.find((m) => m.id === d.materialId)?.name ?? 'Whole submission'} · {d.uploadedBy}, {formatDate(d.uploadedAt)}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )
            )}
          </Box>
        </Box>
        <OriginMap origins={used} labels={{ region: "Map of the submission's origins" }} flagged={flaggedOrigins} selected={selected} onSelect={setSelected} fitKey={s.id} plots={plots ?? null} height="fill" padding={64} showReadout />
      </Box>

      <DecisionDialog s={s} kind={deciding} onClose={() => setDeciding(null)} />
    </AppShell>
  )
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box>
      <Typography variant="overline" component="div" sx={{ color: 'text.disabled' }}>{label}</Typography>
      <Box sx={{ font: `400 13px ${tokens.font.sans}` }}>{children}</Box>
    </Box>
  )
}

function MaterialCard({ s, materialId, open, selected, onSelect }: {
  s: Submission
  materialId: string
  open: boolean
  selected: string | null
  onSelect: (id: string) => void
}) {
  const material = s.materials.find((m) => m.id === materialId)!
  const answer = s.answers[materialId]
  const commodity = s.taxonomy.commodities.find((c) => c.id === material.commodityId)
  const ingredientOf = new Map(s.taxonomy.ingredients.map((f) => [f.id, f]))
  const originOf = new Map(s.origins.map((o) => [o.id, o]))
  const [tree, setTree] = useState(false)

  return (
    <Box component="section" aria-label={material.name} sx={{ backgroundColor: 'background.paper', border: `1px solid ${tokens.color.ink200}`, borderRadius: `${tokens.radius.md}px` }}>
      <Box sx={{ px: 4, py: 3, borderBottom: `1px solid ${tokens.color.ink200}`, display: 'flex', alignItems: 'baseline', gap: 2.5, flexWrap: 'wrap' }}>
        <Typography variant="h4" component="h2">{material.name}</Typography>
        <Typography sx={{ font: `400 12px ${tokens.font.mono}`, color: 'text.secondary' }}>{material.code}</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', flex: 1 }}>
          {material.kind === 'fixed' ? `Fixed · ${commodity?.name ?? ''}` : 'Blend'} · {tonnes(material.annualVolumeT)} a year
        </Typography>
        {(answer?.composition.length ?? 0) > 0 && (
          <Button size="small" variant="text" startIcon={<GitFork size={14} style={{ transform: 'rotate(90deg)' }} />} onClick={() => setTree(true)}>
            Composition tree
          </Button>
        )}
      </Box>
      {tree && (
        <DiagramDialog title={`How ${material.name} is made up`} fileName={`${s.supplier.name}-${material.code}-composition`} onClose={() => setTree(false)}>
          {(ref) => (
            <CompositionTree
              ref={ref}
              material={material}
              answer={answer}
              taxonomy={s.taxonomy}
              origins={s.origins}
              labels={{
                title: `How ${material.name} is made up`,
                commodities: 'Commodities', ingredients: 'Ingredients', origins: 'Origins',
                noOrigins: 'No origins', countryName: (code) => countryName(code, 'en'),
              }}
            />
          )}
        </DiagramDialog>
      )}
      {(answer?.composition ?? []).map((line) => {
        const ingredient = ingredientOf.get(line.ingredientId)
        const allocations = answer?.allocations[line.ingredientId] ?? []
        const compositionTarget: FlagTarget = { kind: 'composition', materialId, ingredientId: line.ingredientId }
        return (
          <Box key={line.ingredientId} sx={{ borderTop: `1px solid ${tokens.color.ink100}`, '&:first-of-type': { borderTop: 0 } }}>
            <Line
              s={s}
              target={compositionTarget}
              open={open}
              title={<Box component="span" sx={{ fontWeight: 500 }}>{ingredient?.name}</Box>}
              detail={`${s.taxonomy.commodities.find((c) => c.id === ingredient?.commodityId)?.name ?? ''} · ${ingredient?.code ?? ''}`}
              value={pct(line.sharePct)}
              valueDetail={tonnes((material.annualVolumeT * line.sharePct) / 100)}
              changed={line.changed}
            />
            <Box sx={{ pl: 8, pb: 2 }}>
              {allocations.length === 0 && (
                <Typography sx={{ px: 4, py: 2, color: tokens.color.danger, font: `500 13px ${tokens.font.sans}` }}>No origins</Typography>
              )}
              {allocations.map((a) => {
                const origin = originOf.get(a.originId)
                if (!origin) return null
                return (
                  <Line
                    key={a.originId}
                    s={s}
                    target={{ kind: 'origin', materialId, ingredientId: line.ingredientId, originId: a.originId }}
                    open={open}
                    selected={selected === a.originId}
                    onClick={() => onSelect(a.originId)}
                    title={(
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5 }}>
                        {origin.kind === 'area' ? <Hexagon size={14} strokeWidth={1.75} /> : origin.kind === 'farms' ? <Sprout size={14} strokeWidth={1.75} /> : <MapPin size={14} strokeWidth={1.75} />}
                        {origin.name}
                      </Box>
                    )}
                    detail={(
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5 }}>
                        <CountryFlag code={origin.countryCode} size={11} />{origin.address ?? origin.countryCode}
                      </Box>
                    )}
                    check={origin.check}
                    supply={origin.supply ?? null}
                    originId={origin.id}
                    value={pct(a.volumeSharePct)}
                    valueDetail={tonnes(a.tonnes ?? 0)}
                    changed={a.changed}
                  />
                )
              })}
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

/** One reviewable line — a share or an origin — with its flag, if any, and a way to add one. */
function Line({ s, target, open, title, detail, value, valueDetail, check, supply, originId, changed, selected, onClick }: {
  s: Submission
  target: FlagTarget
  open: boolean
  title: ReactNode
  detail: ReactNode
  value: string
  valueDetail: string
  check?: { state: 'passed' } | { state: 'failed'; reason: string } | { state: 'pending' }
  supply?: import('@agribot/sourcing-model').SupplySummary | null
  originId?: string
  changed?: { by: string; at: string }
  selected?: boolean
  onClick?: () => void
}) {
  const add = useAddFlag(s.id)
  const remove = useRemoveFlag(s.id)
  const [writing, setWriting] = useState(false)
  const [note, setNote] = useState('')
  const [farms, setFarms] = useState(false)
  const flags = s.flags.filter((f) => targetKey(f.target) === targetKey(target))

  return (
    <Box sx={{ px: 4, py: 2.5, backgroundColor: selected ? tokens.color.brandTeal100 : 'transparent', borderRadius: `${tokens.radius.sm}px` }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
        <Box
          component={onClick ? 'button' : 'div'}
          type={onClick ? 'button' : undefined}
          onClick={onClick}
          sx={{ flex: 1, minWidth: 0, textAlign: 'left', border: 0, background: 'none', p: 0, color: 'inherit', cursor: onClick ? 'pointer' : 'default', font: `400 14px ${tokens.font.sans}` }}
        >
          <Box>{title}</Box>
          <Box sx={{ color: 'text.secondary', font: `400 12px ${tokens.font.sans}`, mt: 0.5 }}>{detail}</Box>
          {check && <CheckLine check={check} />}
        </Box>
        <Tooltip title={changed ? `Changed by ${changed.by} on ${formatDateTime(changed.at)}` : ''} placement="left">
          <Box sx={{ textAlign: 'right', flex: 'none' }}>
            <Box sx={{ font: `600 14px ${tokens.font.sans}`, fontVariantNumeric: 'tabular-nums' }}>{value}</Box>
            <Box sx={{ font: `400 12px ${tokens.font.sans}`, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{valueDetail}</Box>
          </Box>
        </Tooltip>
        {open && (
          <Tooltip title="Flag this for the supplier">
            <IconButton aria-label="Flag this for the supplier" size="small" onClick={() => setWriting(true)} sx={{ width: 36, height: 36, flex: 'none' }}>
              <FlagIcon size={16} strokeWidth={1.75} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {supply && supply.plots + supply.links.length > 0 && (
        <SupplySummaryLine supply={supply} open={farms} onToggle={() => { setFarms((v) => !v); onClick?.() }} />
      )}
      {farms && originId && <SupplyDrill originId={originId} />}
      {flags.map((f) => <FlagBox key={f.id} flag={f} onRemove={open ? () => remove.mutate(f.id) : undefined} />)}
      {writing && (
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault()
            add.mutate({ target, note }, { onSuccess: () => { setWriting(false); setNote('') } })
          }}
          sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <TextField
            label="What needs a second look?"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            minRows={2}
            autoFocus
            InputLabelProps={{ shrink: true }}
            sx={{ '& .MuiOutlinedInput-input': { height: 'auto', p: 0 }, '& .MuiOutlinedInput-root': { p: 2.5 } }}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button type="submit" size="small" disabled={!note.trim() || add.isPending}>Add flag</Button>
            <Button size="small" variant="text" onClick={() => { setWriting(false); setNote('') }}>Cancel</Button>
          </Box>
          {add.isError && <Typography variant="body2" color="error">{(add.error as Error).message}</Typography>}
        </Box>
      )}
    </Box>
  )
}

function CheckLine({ check }: { check: NonNullable<Parameters<typeof Line>[0]['check']> }) {
  const style = { display: 'flex', alignItems: 'flex-start', gap: 1, mt: 0.75, font: `500 12px ${tokens.font.sans}` }
  if (check.state === 'failed') {
    return <Box sx={{ ...style, color: tokens.color.danger }}><Box sx={{ flex: 'none', mt: '1px' }}><CircleAlert size={13} strokeWidth={2} /></Box>{check.reason}</Box>
  }
  if (check.state === 'pending') return <Box sx={{ ...style, color: 'text.secondary' }}><Clock size={13} strokeWidth={2} />Not checked yet</Box>
  return <Box sx={{ ...style, color: tokens.color.success }}><CircleCheck size={13} strokeWidth={2} />Checks passed</Box>
}

function FlagBox({ flag, onRemove }: { flag: Flag; onRemove?: () => void }) {
  return (
    <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'flex-start', p: 2.5, borderRadius: `${tokens.radius.md}px`, border: '1px solid #f0d999', backgroundColor: '#fdf6e4', color: '#7d5a1a', font: `400 13px ${tokens.font.sans}` }}>
      <Box sx={{ flex: 'none', mt: '1px' }}><MessageSquareWarning size={15} strokeWidth={1.75} /></Box>
      <Box sx={{ flex: 1 }}>
        <Box sx={{ fontWeight: 600 }}>{flag.raisedBy} · {formatDate(flag.raisedAt)}{flag.addressed ? ' · answered' : ''}</Box>
        <Box>{flag.note}</Box>
        {flag.reply && <Box sx={{ mt: 1, color: tokens.color.ink700 }}>Supplier: {flag.reply}</Box>}
      </Box>
      {onRemove && (
        <IconButton aria-label="Remove this flag" size="small" onClick={onRemove} sx={{ color: 'inherit', width: 32, height: 32 }}>
          <Trash2 size={15} strokeWidth={1.75} />
        </IconButton>
      )}
    </Box>
  )
}

function Decisions({ s }: { s: Submission }) {
  if (!s.reviews.length) return <Typography variant="body2" sx={{ color: 'text.secondary' }}>No decisions yet. The first review decides here.</Typography>
  return (
    <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {[...s.reviews].reverse().map((r, i) => (
        <Box component="li" key={i} sx={{ p: 4, backgroundColor: 'background.paper', border: `1px solid ${tokens.color.ink200}`, borderRadius: `${tokens.radius.md}px` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, font: `600 14px ${tokens.font.sans}` }}>
            {r.decision === 'approve'
              ? <CircleCheck size={16} strokeWidth={2} color={tokens.color.success} />
              : <Undo2 size={16} strokeWidth={2} color={tokens.color.warning} />}
            {r.decision === 'approve' ? 'Approved' : 'Returned to the supplier'} at {LEVEL_LABEL[r.level].toLowerCase()}
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>{r.by} ({LEVEL_ROLE[r.level]}) · {formatDateTime(r.at)}</Typography>
          {r.comment && <Typography sx={{ mt: 2, font: `400 14px ${tokens.font.sans}` }}>“{r.comment}”</Typography>}
        </Box>
      ))}
    </Box>
  )
}

function History({ s }: { s: Submission }) {
  return (
    <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {[...s.history].reverse().map((h, i) => (
        <Box component="li" key={i} sx={{ display: 'flex', gap: 4, py: 2.5, borderTop: i ? `1px solid ${tokens.color.ink200}` : 0, font: `400 13px ${tokens.font.sans}` }}>
          <Box sx={{ width: 150, flex: 'none', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{formatDateTime(h.at)}</Box>
          <Box><Box component="span" sx={{ fontWeight: 500 }}>{h.by}</Box> · {h.action}</Box>
        </Box>
      ))}
    </Box>
  )
}

function DecisionDialog({ s, kind, onClose }: { s: Submission; kind: 'approve' | 'return' | null; onClose: () => void }) {
  const approve = useApprove(s.id)
  const back = useReturn(s.id)
  const [comment, setComment] = useState('')
  const level = s.level
  const busy = approve.isPending || back.isPending
  const error = (approve.error ?? back.error) as Error | null
  const close = () => { setComment(''); approve.reset(); back.reset(); onClose() }
  const flags = s.flags.filter((f) => !f.addressed).length

  return (
    <Dialog open={kind !== null} onClose={close} maxWidth="sm" fullWidth>
      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault()
          if (kind === 'approve') approve.mutate(comment || undefined, { onSuccess: close })
          if (kind === 'return') back.mutate(comment, { onSuccess: close })
        }}
      >
        <DialogTitle>
          {kind === 'return' ? `Return to ${s.supplier.name}?` : level === 1 ? 'Approve for final review?' : 'Approve this submission?'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {kind === 'return'
              ? `The supplier is emailed a sign-in link and sees your comment at the top${flags ? `, with your ${flags} flag${flags === 1 ? '' : 's'} beside the items` : ''}. Only what you flagged opens for changes.`
              : level === 1
                ? 'It moves to the region’s sustainable-sourcing lead for the final review.'
                : 'The answers become final and read-only. Next year’s campaign starts from them.'}
          </Typography>
          <TextField
            label={kind === 'return' ? 'Comment for the supplier' : 'Comment (optional)'}
            required={kind === 'return'}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            multiline
            minRows={3}
            autoFocus
            InputLabelProps={{ shrink: true }}
            sx={{ '& .MuiOutlinedInput-input': { height: 'auto', p: 0 }, '& .MuiOutlinedInput-root': { p: 2.5 } }}
          />
          {error && <Alert severity="error">{error.message}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 6, pb: 5, gap: 2 }}>
          <Button variant="text" onClick={close}>Cancel</Button>
          <Button type="submit" disabled={busy || (kind === 'return' && !comment.trim())}>
            {kind === 'return' ? 'Return to supplier' : 'Approve'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}
